import { Box, Button, Typography } from '@material-ui/core'
import { AddIcon } from 'assets/icons/AddIcon'
import { Fragment, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { openModal } from 'store/modules/ModalActions';
import { useParams } from 'react-router-dom';
import { RootState } from 'store';
import { TipoPrograma } from 'store/modules/EscolasTypes';
import { FormDadosCooperativaTyped, FormDadosJornadaEducacaoTyped, FormDadosPufvTyped, FormularioPeriodoHandle } from '../../listaPrograma/listaPeriodo/listaPeriodoItem/cooperativa/Modal/__type';
import { HelpButton } from 'components/HelpButton';
import './index.scss'
import ModalProgramaPeriodo from '../Modais/ModalProgramaPeriodo';
import useProgramas from '../../hooks/useProgramas';
import { useHabilitaCampoPrograma } from '../../hooks/useDisablePrograma';

interface BotaoCadastrarPeriodoProps {
    tipoPrograma: TipoPrograma;
    periodoAno?: string;
}

export const BotaoCadastrarPeriodo = ({ tipoPrograma }: BotaoCadastrarPeriodoProps): JSX.Element => {
    const { id, periodo, idPeriodo } = useParams() as { id: string, periodo: string, idPeriodo: string };
    const { classeCondicional, isDesabilitado } = useHabilitaCampoPrograma(tipoPrograma);
    const { getTitleModal, getPeriodoValidationKey, rotaPeriodoModal, retornaListaPeriodos, cadastroPeriodo, duplicacaoPeriodo, edicaoPeriodo } = useProgramas();
    const formRefCooperativa = useRef<FormularioPeriodoHandle>(null);
    const formRefUniaoFazAVida = useRef<FormularioPeriodoHandle>(null);
    const formRefJornadaEducacao = useRef<FormularioPeriodoHandle>(null);
    const languageValues = useSelector((state: RootState) => state.languages.languageValues);
    const dispatch = useDispatch();

    const formularioMap = useMemo(() => {
        const map = new Map<TipoPrograma, { component: JSX.Element; ref: React.RefObject<FormularioPeriodoHandle> }>();
        map.set(TipoPrograma.UniaoFazAVida, {
            component: <FormDadosPufvTyped ref={formRefUniaoFazAVida} id={id} periodo={periodo} />,
            ref: formRefUniaoFazAVida,
        });
        map.set(TipoPrograma.CooperativaEscolar, {
            component: <FormDadosCooperativaTyped ref={formRefCooperativa} id={id} periodo={periodo} idPeriodo={idPeriodo} />,
            ref: formRefCooperativa,
        });
        map.set(TipoPrograma.JornadaEducacao, {
            component: <FormDadosJornadaEducacaoTyped ref={formRefJornadaEducacao} id={id} periodo={periodo} idPeriodo={idPeriodo} />,
            ref: formRefJornadaEducacao,
        });
        return map;
    }, [id, periodo, idPeriodo]);

    const isEditing = useMemo(() => !!periodo && periodo.length > -1, [periodo]);

    const labelButton = useMemo(() => {
        if (duplicacaoPeriodo) return languageValues.duplicar;
        if (isEditing) return languageValues.salvar;
        return languageValues.cadastrar;
    }, [duplicacaoPeriodo, isEditing, languageValues]);

    const handleClick = () => rotaPeriodoModal(tipoPrograma, id);

    const openModalFormDados = useCallback((tipoProgramaParam: TipoPrograma) => {
        const formConfig = formularioMap.get(tipoProgramaParam);
        if (!formConfig) return;
        dispatch(openModal(ModalProgramaPeriodo, {
            modalKey: 'ModalProgramaPeriodo',
            validationKey: getPeriodoValidationKey(tipoProgramaParam),
            title: getTitleModal(tipoProgramaParam),
            content: formConfig.component,
            confirmLabel: labelButton,
            cancelLabel: 'Cancelar',
            confirmThemeButton: 'button_PUFV',
            cancelThemeButton: 'button_PUFV',
            confirmCallback: async () => {
                formConfig.ref.current?.submitForm();
            },
            cancelCallback: () => {
                retornaListaPeriodos(id)
            },
        }));
    }, [dispatch, formularioMap, getPeriodoValidationKey, getTitleModal, id, labelButton, retornaListaPeriodos]);

    useEffect(() => {
        if (cadastroPeriodo || edicaoPeriodo || duplicacaoPeriodo) {
            openModalFormDados(tipoPrograma);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cadastroPeriodo, edicaoPeriodo, duplicacaoPeriodo, tipoPrograma]);

    return (
        <Fragment>
            <Box className='BotaoCadastrarPeriodo_container-title'>
                <Typography className={`${classeCondicional} BotaoCadastrarPeriodo_label--big`}>
                    Histórico anual do programa
                    <HelpButton
                        text={'Os dados inseridos abaixo são referentes aos colaboradores(as), professores(as) e alunos(as) que participaram do programa na escola'}
                        className='BotaoCadastrarPeriodo_helpButton'
                    />
                </Typography>
                <Typography className={`${classeCondicional} BotaoCadastrarPeriodo_label--small`}>
                    Cadastre e edite as informações sobre o programa educacional:</Typography>
            </Box>

            <Box className='BotaoCadastrarPeriodo_container-button'>
                <Button
                    disabled={isDesabilitado}
                    className='button_add'
                    endIcon={<AddIcon />}
                    onClick={() => handleClick()}
                    variant='contained'
                >
                    Cadastrar Dados
                </Button>
            </Box>
        </Fragment>
    )
}
